// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup
} from 'firebase/auth';
// --- ¡CAMBIO! Añadimos Timestamp y onSnapshot ---
import {
  doc, getDoc, setDoc, collection, query,
  where, getDocs, Timestamp, onSnapshot, updateDoc, deleteDoc
} from 'firebase/firestore';

// 1. Creamos el contexto
const AuthContext = createContext();

// 2. Creamos un "hook" personalizado para usar el contexto fácilmente
export function useAuth() {
  return useContext(AuthContext);
}

// 3. Creamos el "Proveedor" que envolverá nuestra app
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null); // Aquí guardaremos el ROL y condominioId
  const [originalUserData, setOriginalUserData] = useState(null); // MULTI-COTO: Para guardar los permisos base antes de entrar a un coto
  const [loading, setLoading] = useState(true);
  const [isMoroso, setIsMoroso] = useState(false); // <-- ¡NUEVO ESTADO!
  const [hasConvenio, setHasConvenio] = useState(false); // <-- ESTATUS CONVENIO

  // Función para validar el código y buscar el condominio
  async function validarCodigoActivacion(codigo) {
    const q = query(collection(db, "condominios"), where("codigoActivacion", "==", codigo));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Código de activación inválido.");
    }
    // Retorna el ID del condominio (ej: "CONDO_ABC")
    return querySnapshot.docs[0].id;
  }

  // --- REGISTRO CON EMAIL Y CONTRASEÑA ---
  async function register(email, password, nombre, codigoActivacion, cuentaPadreId = null) {
    const cleanEmail = email.toLowerCase().trim();

    // 1. Verificar si es un usuario corporativo pre-configurado
    const preConfigRef = doc(db, "usuarios_preconfigurados", cleanEmail);
    const preConfigSnap = await getDoc(preConfigRef);

    let finalRole = "residente";
    let finalCondoId = null;
    let finalPermisos = null;

    if (preConfigSnap.exists()) {
      const preData = preConfigSnap.data();
      finalRole = preData.rol || "superuser";
      finalCondoId = preData.condominioId || "GLOBAL";
      finalPermisos = preData.permisos || null;
      console.log("Staff pre-aprobado detectado. Omitiendo validación de código.");
    } else {
      // 2. Si NO es staff, validar el código de activación (Residente)
      if (!codigoActivacion) {
        throw new Error("El código de activación es requerido para residentes.");
      }
      finalCondoId = await validarCodigoActivacion(codigoActivacion);
    }

    // 3. Crear el usuario en Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // 4. Guardar los datos del usuario en Firestore
    let parentCasa = "";
    if (cuentaPadreId) {
      try {
        const parentRef = doc(db, "usuarios", cuentaPadreId);
        const parentSnap = await getDoc(parentRef);
        if (parentSnap.exists()) {
          parentCasa = parentSnap.data().numeroCasa || "";
        }
      } catch (err) {
        console.error("Error fetching parent unit:", err);
      }
    }

    const newUserDoc = {
      uid: user.uid,
      nombre: nombre.trim(),
      email: user.email,
      rol: finalRole,
      condominioId: finalCondoId,
      metodoRegistro: "email",
      permisos: finalPermisos,
      numeroCasa: parentCasa,
      numeroCelular: "",
      tipoResidente: finalRole === "residente" ? (cuentaPadreId ? "Inquilino" : "Por definir") : "Staff",
      estatusValidacion: finalRole === "residente" ? "pendiente" : "aprobado",
      fotoPerfilUrl: "",
      acceptedLegal: true,
      acceptedLegalAt: Timestamp.now(),
      fechaRegistro: Timestamp.now()
    };
    if (cuentaPadreId) {
      newUserDoc.cuentaPadreId = cuentaPadreId;
    }
    
    await setDoc(doc(db, "usuarios", user.uid), newUserDoc);

    // 5. LIMPIEZA: Eliminar registros temporales de staff (ID: TEMP_...)
    // NOTA: Como el usuario acaba de ser creado, Firestore Rules puede rechazar el deleteDoc
    // (allow delete: if isAdmin() ). Lo envolvemos en un try/catch aislado para que NO
    // rompa la experiencia de login si falla.
    try {
      const qTemp = query(
        collection(db, "usuarios"),
        where("email", "==", cleanEmail),
        where("isPendingActivation", "==", true)
      );
      const tempSnap = await getDocs(qTemp);
      const deletePromises = tempSnap.docs
        .filter(doc => doc.id !== user.uid) // Por seguridad
        .map(doc => deleteDoc(doc.ref));
      
      if (deletePromises.length > 0) {
        // Usamos allSettled en lugar de all para que un error no aborte a los demás
        await Promise.allSettled(deletePromises);
        console.log(`Intento de limpieza de registros temporales para ${cleanEmail} finalizado.`);
      }
    } catch (err) {
      console.warn("Limpieza de registros temporales falló (probablemente por reglas de Firestore). Esto es normal.", err);
    }

    return userCredential;
  }

  // --- LOGIN CON EMAIL Y CONTRASEÑA ---
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // --- LOGIN CON GOOGLE ---
  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);

    const user = userCredential.user;
    const docRef = doc(db, "usuarios", user.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { user: user, esNuevo: true };
    }
    return { user: user, esNuevo: false };
  }

  // --- LOGIN CON FACEBOOK ---
  async function loginWithFacebook() {
    const provider = new FacebookAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);

    const user = userCredential.user;
    const docRef = doc(db, "usuarios", user.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { user: user, esNuevo: true };
    }
    return { user: user, esNuevo: false };
  }

  // --- LOGIN CON APPLE ---
  async function loginWithApple() {
    const provider = new OAuthProvider('apple.com');
    const userCredential = await signInWithPopup(auth, provider);

    const user = userCredential.user;
    const docRef = doc(db, "usuarios", user.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { user: user, esNuevo: true };
    }
    return { user: user, esNuevo: false };
  }

  // --- FUNCIÓN PARA COMPLETAR REGISTRO SOCIAL (O VERIFICAR TEST USER) ---
  async function completarRegistroSocial(codigoActivacion, cuentaPadreId = null) {
    if (!currentUser) throw new Error("No hay usuario conectado");

    // 1. Validar el código primero
    const condominioId = await validarCodigoActivacion(codigoActivacion);

    // 2. Si el usuario ya tiene datos (es Test User verificado), solo actualizamos
    if (userData) {
      // Validar que el código corresponda al condominio correcto (Opcional, pero bueno)
      if (condominioId !== userData.condominioId) {
        throw new Error("El código no corresponde a tu condominio asignado.");
      }

      const userRef = doc(db, "usuarios", currentUser.uid);
      await updateDoc(userRef, {
        requiresVerification: false, // Quitamos el bloqueo
        metodoRegistro: "test_verified"
      });

      // Actualizar estado local
      setUserData({ ...userData, requiresVerification: false });
      return;
    }

    // 3. Si NO tiene datos (Registro nuevo Google/FB), creamos todo
    let parentCasa = "";
    if (cuentaPadreId) {
      try {
        const parentRef = doc(db, "usuarios", cuentaPadreId);
        const parentSnap = await getDoc(parentRef);
        if (parentSnap.exists()) {
          parentCasa = parentSnap.data().numeroCasa || "";
        }
      } catch (err) {
        console.error("Error fetching parent unit:", err);
      }
    }

    const newUserDoc = {
      uid: currentUser.uid,
      nombre: currentUser.displayName,
      email: currentUser.email,
      rol: "residente",
      condominioId: condominioId,
      metodoRegistro: currentUser.providerData[0].providerId,
      numeroCasa: parentCasa,
      numeroCelular: "",
      tipoResidente: cuentaPadreId ? "Inquilino" : "Por definir",
      estatusValidacion: "pendiente",
      fotoPerfilUrl: "",
      acceptedLegal: true,
      acceptedLegalAt: Timestamp.now(),
      fechaRegistro: Timestamp.now()
    };
    if (cuentaPadreId) {
      newUserDoc.cuentaPadreId = cuentaPadreId;
    }
    await setDoc(doc(db, "usuarios", currentUser.uid), newUserDoc);
    setUserData(newUserDoc);
  }

  // --- CERRAR SESIÓN ---
  function logout() {
    setUserData(null);
    return signOut(auth);
  }

  // --- ¡NUEVA FUNCIÓN PARA RECARGAR LOS DATOS! ---
  async function refreshUserData() {
    if (!currentUser) return;
    try {
      const docRef = doc(db, "usuarios", currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        let data = docSnap.data();
        if (data.condominioId) {
            try {
                const condoSnap = await getDoc(doc(db, "condominios", data.condominioId));
                if (condoSnap.exists()) {
                    const condoData = condoSnap.data();
                    data.condominioNombre = condoData.nombre;
                    data.condoData = condoData;
                }
            } catch (err) {
                console.error("Error fetching condo name in refresh:", err);
            }
        }
        setUserData(data);
      }
    } catch (e) {
      console.error("Error al recargar datos del usuario:", e);
    }
  }
  // --- FIN DE NUEVA FUNCIÓN ---

  // --- EFECTO PRINCIPAL: EL VIGILANTE DE AUTH ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const docRef = doc(db, "usuarios", user.uid);
          
          // Helper para reintentar la lectura de Firestore
          const fetchWithRetry = async (retries = 5, delayMs = 500) => {
            for (let i = 0; i < retries; i++) {
                let docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    return docSnap;
                }
                // Fallback: Si no se encuentra por UID, buscar por email en Firestore
                if (user.email) {
                    try {
                        const qEmail = query(collection(db, "usuarios"), where("email", "==", user.email.toLowerCase().trim()));
                        const emailSnap = await getDocs(qEmail);
                        if (!emailSnap.empty) {
                            const existingData = emailSnap.docs[0].data();
                            console.log("Encontrado usuario en Firestore por email:", existingData);
                            await setDoc(docRef, { ...existingData, uid: user.uid }, { merge: true });
                            const newSnap = await getDoc(docRef);
                            if (newSnap.exists()) return newSnap;
                        }
                    } catch (eErr) {
                        console.warn("Fallback email search error:", eErr);
                    }
                }
                console.log(`Documento no encontrado, intentando de nuevo en ${delayMs}ms... (Intento ${i + 1}/${retries})`);
                await new Promise(res => setTimeout(res, delayMs));
            }
            return null; // Después de todos los reintentos, retornamos null
          };

          const docSnap = await fetchWithRetry();

          if (docSnap && docSnap.exists()) {
            let data = docSnap.data();
            console.log("User data found:", data);

            // Fetch condominium name and active modules if condominioId exists
            if (data.condominioId) {
                try {
                    const condoSnap = await getDoc(doc(db, "condominios", data.condominioId));
                    if (condoSnap.exists()) {
                        const condoData = condoSnap.data();
                        data.condominioNombre = condoData.nombre;
                        data.condoData = condoData;
                    }
                } catch (err) {
                    console.error("Error fetching condo name for context:", err);
                }
            }

            // AUTO-INJECT PERMISSIONS FOR SUPERUSER/MASTERUSER (MIGRATION)
            if ((data.rol === 'superuser' || data.rol === 'masteruser') && !data.permisos) {
                const ALL_PERMISSIONS = {
                    crear_usuarios: true, editar_permisos: true, aprobar_nomina: true,
                    crear_empleados: true, editar_empleados: true, acceso_modulo_rh: true,
                    acceso_modulo_nomina: true, acceso_modulo_dispersion: true,
                    acceso_modulo_crm: true, acceso_modulo_catalogos: true,
                    acceso_modulo_marketplace: true, aprobar_proveedores: true,
                    timbrar_facturas: true, postear_jes: true, alta_proveedores: true,
                    editar_cuentas_catalogo: true, editar_crm: true, editar_servicios: true,
                    editar_tiers_marketplace: true
                };
                data.permisos = ALL_PERMISSIONS;
                try {
                    await updateDoc(docRef, { permisos: ALL_PERMISSIONS });
                    console.log(`Permisos migraron exitosamente para el ${data.rol}.`);
                } catch (err) {
                    console.error("No se pudieron auto-inyectar los permisos:", err);
                }
            }

            setOriginalUserData(data);
            setUserData(data);

            // TRACK LOGIN EVENT (SaaS Analytics)
            // Solo registrar 1 vez por sesión (si currentUser era null)
            if (!currentUser) {
                try {
                    const loginRef = collection(db, 'logins_historico');
                    await setDoc(doc(loginRef), {
                        uid: user.uid,
                        condominioId: data.condominioId || 'UNKNOWN',
                        rol: data.rol || 'N/A',
                        timestamp: Timestamp.now()
                    });
                } catch(err) {
                    console.error("Error logging session:", err);
                }
            }

          } else {
            console.log("Definitivamente no se encontró user data para uid:", user.uid);
            setUserData(null);
          }
        } catch (e) {
          console.error("Error fetching user data:", e);
          setUserData(null);
        }
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setUserData(null);
        setIsMoroso(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // --- ¡NUEVO useEffect (Maneja el estatus de Moroso)! ---
  // Se ejecuta cada vez que 'currentUser' y 'userData' cambian
  useEffect(() => {
    let unsubscribeMoroso = () => { };
    let unsubscribeConvenios = () => { };

    const parseDateMillis = (val) => {
      if (!val) return 0;
      if (val.toMillis) return val.toMillis();
      if (val.seconds) return val.seconds * 1000;
      if (val instanceof Date) return val.getTime();
      
      let t = new Date(val).getTime();
      if (!isNaN(t)) return t;
      
      if (typeof val === 'string') {
        // Handle DD/MM/YY or DD/MM/YYYY
        const parts = val.split('/');
        if (parts.length === 3) {
          let day = parseInt(parts[0], 10);
          let month = parseInt(parts[1], 10) - 1;
          let year = parseInt(parts[2], 10);
          if (year < 100) year += 2000;
          t = new Date(year, month, day).getTime();
          if (!isNaN(t)) return t;
        }
      }
      return 0;
    };

    if (currentUser && userData && userData.rol === 'residente') {
      let qMoroso;
      if (userData.numeroCasa && userData.numeroCasa !== 'N/A') {
        qMoroso = query(
          collection(db, "cargos"),
          where("condominioId", "==", userData.condominioId),
          where("unidadResidente", "==", userData.numeroCasa)
        );
      } else {
        qMoroso = query(
          collection(db, "cargos"),
          where("residenteId", "==", currentUser.uid)
        );
      }

      const qConvenios = query(
        collection(db, "convenios"),
        where("condominioId", "==", userData.condominioId),
        where("residenteId", "==", currentUser.uid),
        where("estatus", "==", "activo")
      );

      let activeConveniosCount = 0;
      let hayAdeudosMorosos = false;

      const updateEstatusMoroso = async () => {
        let nuevoEstatus = hayAdeudosMorosos;
        const hasActiveConvenio = activeConveniosCount > 0;

        // Si tiene algún convenio activo, verificar la regla del condominio
        if (hasActiveConvenio) {
          try {
            const condoSnap = await getDoc(doc(db, "condominios", userData.condominioId));
            if (condoSnap.exists()) {
              const activeAccess = condoSnap.data().conveniosConfig?.activeAccess || 'completo';
              if (activeAccess === 'completo') {
                // Se considera "Al Corriente"
                nuevoEstatus = false;
              }
            }
          } catch (e) {
            console.error("Error reading condo config for moroso check:", e);
          }
        }

        setIsMoroso(nuevoEstatus);
        setHasConvenio(hasActiveConvenio);

        // SYNC WITH FIRESTORE (only if NOT simulated)
        if (userData && !userData.isSimulated && (userData.isMoroso !== nuevoEstatus || userData.hasConvenio !== hasActiveConvenio)) {
          console.log(`Syncing isMoroso to Firestore: ${nuevoEstatus}, hasConvenio: ${hasActiveConvenio}`);
          try {
            const userRef = doc(db, "usuarios", currentUser.uid);
            await updateDoc(userRef, { isMoroso: nuevoEstatus, hasConvenio: hasActiveConvenio });
          } catch (err) {
            console.error("Error syncing isMoroso/hasConvenio:", err);
          }
        }
      };

      unsubscribeConvenios = onSnapshot(qConvenios, (snapConvenios) => {
        activeConveniosCount = snapConvenios.size;
        updateEstatusMoroso();
      });

      unsubscribeMoroso = onSnapshot(qMoroso, (snapshot) => {
        const ahora = Timestamp.now().toMillis();
        hayAdeudosMorosos = snapshot.docs.some(d => {
           const data = d.data();
           const vencimiento = parseDateMillis(data.fechaVencimiento);
           const deuda = (Number(data.monto) || 0) - (Number(data.montoPagado) || 0);
           return (data.estatus === 'pendiente' || data.estatus === 'vencido') && deuda > 1.0 && vencimiento < ahora;
        });
        updateEstatusMoroso();
      }, (error) => {
        console.error("Error al verificar estatus moroso:", error);
        setIsMoroso(false);
        setHasConvenio(false);
      });

    } else {
      setIsMoroso(false);
      setHasConvenio(false);
    }

    return () => {
      unsubscribeMoroso();
      unsubscribeConvenios();
    };
  }, [currentUser, userData]);

  // --- FUNCION MULTI-COTO: CAMBIAR ESPACIO DE TRABAJO ---
  const switchWorkspace = async (condominioId) => {
    if (!originalUserData) return;

    let condominioNombre = null;
    if (condominioId && condominioId !== 'GLOBAL' && condominioId !== 'CORPORATIVO') {
      try {
        const condoSnap = await getDoc(doc(db, "condominios", condominioId));
        if (condoSnap.exists()) {
          condominioNombre = condoSnap.data().nombre;
        }
      } catch (err) {
        console.error("Error fetching condo name in switchWorkspace:", err);
      }
    }

    if (condominioId === 'GLOBAL' || condominioId === 'CORPORATIVO' || !condominioId) {
      setUserData({ ...originalUserData, condominioId: 'GLOBAL', condominioNombre: null });
      return;
    }

    if (originalUserData.rol === 'masteruser') {
      setUserData({ ...originalUserData, condominioId, condominioNombre });
      return;
    }

    if (originalUserData.rol === 'superuser') {
      if (originalUserData.accesosMultiCondo && originalUserData.accesosMultiCondo.includes(condominioId)) {
        // Al entrar a su coto asignado, actúan como 'admin' localmente pero mantienen
        // sus permisos nativos del SaaS.
        setUserData({
          ...originalUserData,
          condominioId,
          condominioNombre,
          rol: 'admin'
        });
      } else {
        // Si no está en su lista, no lo dejamos cambiar (fall-safe)
        console.warn("Intento de acceso a coto no autorizado.");
      }
    } else {
      setUserData({ ...originalUserData, condominioId, condominioNombre });
    }
  };

  // --- GOD MODE (Simulación de Usuarios) ---
  const simulateUser = async (userDoc) => {
    let condominioNombre = null;
    if (userDoc.condominioId && userDoc.condominioId !== 'GLOBAL') {
      try {
        const condoSnap = await getDoc(doc(db, "condominios", userDoc.condominioId));
        if (condoSnap.exists()) {
          condominioNombre = condoSnap.data().nombre;
        }
      } catch (err) {
        console.error("Error fetching condo name in simulateUser:", err);
      }
    }

    setUserData({ ...userDoc, condominioNombre, isSimulated: true, realUid: currentUser.uid });
    setCurrentUser({
      ...currentUser,
      uid: userDoc.uid || userDoc.id, // Suplantación de identidad para consultas React
      email: userDoc.email,
      displayName: userDoc.nombre,
      isSimulated: true
    });
  };

  const exitSimulation = () => {
    if (originalUserData) {
        setUserData(originalUserData);
    }
    setCurrentUser(auth.currentUser); // Restauramos el Real User Object
  };

  // Mantenemos esto por retrocompatibilidad momentánea
  const setTemporalCondominio = switchWorkspace;

  // 4. Valores que proveerá el contexto
  const value = {
    currentUser,
    userData,
    isMoroso, // <-- ¡AÑADIDO!
    hasConvenio, // <-- ¡AÑADIDO!
    loading,  // <-- ¡AÑADIDO!
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    completarRegistroSocial,
    refreshUserData,
    setTemporalCondominio, // <-- Exportada (Antigua)
    switchWorkspace, // <-- Exportada (Nueva)
    simulateUser,
    exitSimulation,
    setUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}